/**
 * Customers — search and the customer account (360° view).
 *
 * Live: n8n "[C] App 12 - Customers" (REACT_APP_CUSTOMERS_URL), every customer in Zoho:
 *   search   name / phone / email / address / postal code → [{ id, name, phone, email, address, postalCode, status, … }]
 *   account  one customer (their Zoho Inspection id) → { customer, quotes, notes, tasks, timeline }
 * Test mode: the app's own leads, so the screens still work without Zoho.
 *
 * A customer's id is their Zoho Inspection id — the same id the Route, Calls and Communications use.
 */
import { useSyncExternalStore } from 'react';
import { getAccessToken } from './auth';
import { getLeads } from './leads';

const CUSTOMERS_URL = process.env.REACT_APP_CUSTOMERS_URL;
export const CUSTOMERS_LIVE = !!CUSTOMERS_URL;

const call = async (body, signal) => {
  const token = await getAccessToken();
  if (!token) throw new Error('Sign in again to look up customers');
  let res;
  try {
    res = await fetch(CUSTOMERS_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') throw err;
    throw new Error('No connection — try again');
  }
  const out = await res.json().catch(() => ({}));
  if (!res.ok || !out.ok) throw new Error(out.error || `Couldn’t reach Zoho (${res.status})`);
  return out;
};

/* ---------- test mode: search the app's own leads ---------- */
const digits = (s) => String(s || '').replace(/\D/g, '');
const fromLead = (l) => ({
  id: l.id,
  name: l.contactName || '',
  phone: l.servicePhone || '',
  email: l.email || '',
  address: l.serviceAddress || '',
  postalCode: l.postalCode || '',
  status: l.stage || '',
  appointmentStart: l.appointmentStart || null,
  agent: l.agentName || '',
});

export const matchesQuery = (c, q) => {
  const t = String(q || '').trim().toLowerCase();
  if (!t) return false;
  const d = digits(t);
  if (d.length >= 4 && digits(c.phone).includes(d)) return true;
  const compact = t.replace(/\s/g, '');
  return [c.name, c.email, c.address, c.postalCode].some(
    (v) => String(v || '').toLowerCase().includes(t) || String(v || '').toLowerCase().replace(/\s/g, '').includes(compact)
  );
};

/* ---------- search ---------- */
export const searchCustomers = async (q, signal) => {
  const query = String(q || '').trim();
  if (query.length < 2) return [];
  if (!CUSTOMERS_LIVE) return getLeads().map(fromLead).filter((c) => matchesQuery(c, query)).slice(0, 25);
  const out = await call({ action: 'search', q: query }, signal);
  return out.customers || [];
};

/* ---------- the active agents (Zoho Agents) — the admin's "assign agent" list ---------- */
let agentsCache = null;
// re-read from Zoho every few minutes, so an agent added in Zoho shows up without restarting the app
const AGENTS_TTL = 5 * 60 * 1000;
let agentsLoadedAt = 0;
export const loadAgents = async () => {
  if (agentsCache && Date.now() - agentsLoadedAt < AGENTS_TTL) return agentsCache;
  if (!CUSTOMERS_LIVE) {
    agentsCache = [...new Set(getLeads().map((l) => l.agentName).filter(Boolean))].sort().map((name) => ({ id: name, name }));
    return agentsCache;
  }
  const out = await call({ action: 'agents' });
  agentsCache = out.agents || [];
  agentsLoadedAt = Date.now();
  return agentsCache;
};

/* ---------- recently opened customers (this phone only) — shown when the search bar is tapped ---------- */
const RECENT_KEY = 'cie_recent_customers_v1';
const RECENT_MAX = 5;
export const getRecentCustomers = () => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};
export const rememberCustomer = (c) => {
  if (!c || !c.id) return;
  const keep = ({ id, name, phone, email, address, postalCode, status }) => ({ id, name, phone, email, address, postalCode, status });
  const list = [keep(c), ...getRecentCustomers().filter((x) => x.id !== c.id)].slice(0, RECENT_MAX);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(list));
  } catch {
    /* storage full / private mode — recent list just stays as it was */
  }
};

/* ---------- account (kept per customer, so going back and forth is instant) ---------- */
const accounts = new Map(); // id → { data, loading, error }
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

const testAccount = (id) => {
  const lead = getLeads().find((l) => l.id === id);
  if (!lead) throw new Error('This customer isn’t on this phone');
  return {
    customer: {
      ...fromLead(lead),
      status: lead.stage || '',
      concern: '',
      bookingNotes: lead.bookingNotes || '',
      answers: String(lead.customerNotes || '')
        .split('\n')
        .filter(Boolean)
        .map((l) => ({ label: l.split(':')[0], value: l.split(':').slice(1).join(':').trim() })),
      home: {},
      findings: {},
      reportToken: null,
    },
    quotes: [],
    notes: (lead.notes || []).map((n, i) => ({ id: `n${i}`, author: n.author || '', text: n.text || '', at: n.at || '' })),
    tasks: [],
    timeline: lead.appointmentStart ? [{ at: lead.appointmentStart, what: 'Inspection' }] : [],
  };
};

export const loadAccount = async (id) => {
  if (!id) return;
  const prev = accounts.get(id) || {};
  accounts.set(id, { ...prev, loading: true, error: '' });
  emit();
  try {
    const data = CUSTOMERS_LIVE ? await call({ action: 'account', inspectionId: id }) : testAccount(id);
    accounts.set(id, { data, loading: false, error: '' });
  } catch (err) {
    accounts.set(id, { ...prev, loading: false, error: err.message || 'Couldn’t load this customer' });
  }
  emit();
};

const EMPTY = { data: null, loading: false, error: '' };
export const useAccount = (id) => useSyncExternalStore(subscribe, () => accounts.get(id) || EMPTY);
